import { memo, useState, useEffect, useRef } from 'react';
import { EditUserDialog } from '@/pages/admin/users/components/EditUserDialog';
import { useAuth } from '@/context/AuthContext';
import { User } from '@/types/User';

import { logger } from '@/utils/logger';
interface ProfileSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const ProfileSettingsDialog: React.FC<ProfileSettingsDialogProps> = memo(({open, onOpenChange}) => {

   const auth = useAuth();
   const [localUser, setLocalUser] = useState<User | null>(auth.user);
   const [isRefreshing, setIsRefreshing] = useState(false);
   const hasRefreshedRef = useRef(false);

   // Only update local user when dialog opens for the first time
   useEffect(() => {
     if (open) {
       // Only set from auth.user if we haven't just refreshed
       if (!hasRefreshedRef.current) {
         setLocalUser(auth.user);
       }
       hasRefreshedRef.current = false; // Reset flag
     }
   }, [open, auth.user]);

   if(auth.loading) {
    // loader display
    return
   }

  return (
        <EditUserDialog
          isCurrentUser
          open={open}
          onOpenChange={onOpenChange}
          user={localUser}
          onDataRefresh={async () => {
            // Fetch updated user data and update local state without closing dialog
            setIsRefreshing(true);
            try {
              const axios = (await import('axios')).default;
              const { getApiEndpoint, getImageURL } = await import('@/utils/ERPUtils');

              if (!localUser?.email) return;

              const response = await axios.get(
                getApiEndpoint("GET_USER_BY_EMAIL") + `?email=${localUser.email}`
              );

              if (response.data?.message?.data) {
                const profile = response.data.message.data;
                // Handle roles - could be string, array, or object
                let roles = [];
                if (typeof profile?.roles === 'string') {
                  try {
                    roles = JSON.parse(profile.roles);
                  } catch (e) {
                    logger.warn('Failed to parse roles string:', profile.roles);
                    roles = [];
                  }
                } else if (Array.isArray(profile?.roles)) {
                  roles = profile.roles;
                } else {
                  roles = [];
                }

                const avatarUrl = getImageURL(profile?.user_image, profile?.modified) || "";

                const updatedUser: User = {
                  id: profile.email,
                  name: profile.full_name || profile.email?.split('@')[0] || 'Unknown',
                  email: profile.email || '',
                  status: profile.enabled ? 'active' : 'inactive',
                  full_name: profile.full_name || profile.first_name || profile.email,
                  department: profile.department || "",
                  first_name: profile?.first_name || "",
                  last_name: profile?.last_name || "",
                  username: profile?.username || "",
                  middle_name: profile?.middle_name || "",
                  gender: profile?.gender || "",
                  birthdate: profile?.birth_date || "",
                  interests: profile?.interest || "",
                  roles: roles?.map((e: any) => e?.name) || [],
                  lastActive: profile.last_login,
                  avatar: avatarUrl,
                  employee_id: profile.email,
                  phone: profile?.phone || "",
                  mobile_number: profile?.mobile_no || "",
                  location: profile?.location || "",
                  bio: profile?.bio || "",
                  timezone: profile?.time_zone || "",
                  job_role: profile?.job_role || "",
                  notes: "",
                  email_confirmed: true,
                  last_login: profile.last_login,
                  is_pending: profile?.pending_user == 1 ? true : false,
                };

                setLocalUser(updatedUser);
                hasRefreshedRef.current = true; // Set flag to prevent overwriting with auth.user

                // Also refresh the GLOBAL auth user, not just this dialog's
                // copy. The Save button calls onDataRefresh (not
                // onUserUpdated), so without this auth.user keeps the
                // pre-save values: the header/sidebar avatar never updates,
                // and reopening this dialog re-seeds localUser from the stale
                // auth.user - which is why edits only appeared after a hard
                // refresh re-bootstrapped AuthProvider.
                await auth.refreshUser();
              }
            } catch (error) {
              logger.error('Error refreshing user data:', error);
            } finally {
              setIsRefreshing(false);
            }
          }}
          onUserUpdated={async () => {
            // Refresh user data in auth context and close dialog
            await auth.refreshUser();
            onOpenChange(false);
          }}
        />
  );
});

ProfileSettingsDialog.displayName = 'ProfileSettingsDialog';
